import React from 'react';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import nextheralogo from '../assets/nextheralogo.png';

const TermsConditions = () => {
  const handleGoBack = () => {
    window.location.href = '/';
  };

  return (
    <PolicyContainer>
      <PolicyHeader>
        <BackButton onClick={handleGoBack}>
          <ArrowLeft size={20} />
          Back to Home
        </BackButton>
        <Logo src={nextheralogo} alt="NexThera" />
      </PolicyHeader>

      <PolicyContent>
        <Title>Terms & Conditions</Title>
        <LastUpdated>Last updated: 24 August 2025</LastUpdated>
        
        <Section>
          <Paragraph>
            Welcome to NexThera's website. By visiting or using this site, you agree to the terms below. If you don't agree, please don't use the site.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Who we are</SectionTitle>
          <Paragraph>
            This site is operated by NEXTHERAPEUTICS, LDA (VAT/NIPC PT518543404), a private limited company registered in Portugal. Our registered head office is Rua Junta de Freguesia, 194, CI3 Nextherapeutics LDA, Vila Pavão, Escariz, 4540-322 Arouca, Portugal. You can contact us at <EmailLink href="mailto:mail@nexthera.tech">mail@nexthera.tech</EmailLink>.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>1) What these terms cover</SectionTitle>
          <Paragraph>
            These Terms govern your access to and use of our public website, its pages, and any content we publish (collectively, the "Site"). They do not govern our B2B health-AI platform or any paid or pilot services. Those are covered by separate written agreements (for example, a Master Services Agreement and a DPA).
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2) Not medical advice</SectionTitle>
          <Paragraph>
            Our Site provides general information about our company, research interests, technology, and initiatives. It does not provide medical advice, diagnosis, or treatment and must not be used as a substitute for professional medical guidance. Always seek advice from qualified healthcare professionals for patient care decisions. If we describe scientific findings, roadmaps, or prototypes, treat them as informational and subject to change.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>3) Using our Site</SectionTitle>
          <Paragraph>
            You may browse the Site for your own lawful purposes. We grant you a limited, non-exclusive, revocable licence to access and display the Site on your device. You agree not to misuse the Site in any way that could damage it, interfere with other users, or breach the law.
          </Paragraph>
          <Paragraph>A few clear "don'ts":</Paragraph>
          <List>
            <ListItem>don't attempt to breach security, probe or scan our systems, or disrupt the Site;</ListItem>
            <ListItem>don't copy, mirror, frame, scrape, harvest, or bulk-download the Site or its content (including for training models) without our written permission;</ListItem>
            <ListItem>don't post or transmit unlawful, harmful, defamatory, infringing, or misleading material;</ListItem>
            <ListItem>don't upload or submit special-category data (e.g. health data) through public forms - our website isn't designed for clinical information.</ListItem>
          </List>
          <Paragraph>
            We may suspend or block access if we reasonably believe these rules are being breached.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>4) Accounts, events, and newsletters</SectionTitle>
          <Paragraph>
            If we offer sign-ups (e.g, newsletter, webinar, demo), you promise that the information you provide is accurate and that you have the authority to provide it. You're responsible for safeguarding any login details we issue to you and for activities under your account. We may disable accounts that are inactive, misused, or for security reasons.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5) Content we publish</SectionTitle>
          <Paragraph>
            Unless stated otherwise, the Site and its content - text, graphics, logos, videos, designs and layouts - are owned by us or used with permission. All rights are reserved. You may link to our public pages in a fair and legal way that does not suggest sponsorship or endorsement. You must not alter our content or remove proprietary notices. If you think any content on the Site infringes your rights, please contact us with details and evidence and we'll review promptly.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6) Your submissions and feedback</SectionTitle>
          <Paragraph>
            If you send us ideas, suggestions, or other submissions (for example, by email or a contact form), you agree that we may use them to develop and improve our products and communications without owing you compensation, and without any confidentiality obligations, unless we have agreed otherwise in writing. Please don't send confidential or patient-identifiable information via public forms.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7) Privacy and cookies</SectionTitle>
          <Paragraph>
            Our Privacy Policy explains what personal data we collect on the Site and how we use it, and our Cookie Policy explains how to control optional cookies. By using the Site you acknowledge those notices.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8) Third-party links and resources</SectionTitle>
          <Paragraph>
            The Site may link to third-party websites, documents, articles, or tools. We provide these for convenience and information. We don't control, endorse, or assume responsibility for third-party sites or their practices. Use them at your own discretion.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9) Availability and changes</SectionTitle>
          <Paragraph>
            We try to keep the Site available, accurate, and up to date. Still, we provide the Site "as is" and "as available." We may change, suspend, or withdraw any part of the Site without notice - for example, for maintenance, to correct errors, or to reflect new information. We also update these Terms from time to time; the date at the top shows the last update. If changes are material, we'll use a suitable notice on the Site.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10) Research content, forward-looking statements, and accuracy</SectionTitle>
          <Paragraph>
            Because we operate in a fast-moving scientific and regulatory environment, the Site may include forward-looking statements (plans, expectations, estimates) or summaries of scientific literature. These are inherently uncertain and provided for general information only. We make reasonable efforts to ensure accuracy, but we can't promise the Site will always be free from errors, omissions, or out-of-date information.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11) Our responsibility to you (limitations of liability)</SectionTitle>
          <Paragraph>Nothing in these Terms limits any liability that cannot be limited under applicable law. Subject to that, we won't be liable for:</Paragraph>
          <List>
            <ListItem>any indirect, incidental, special, consequential, or punitive losses;</ListItem>
            <ListItem>loss of profits, revenues, data, business, or goodwill; or</ListItem>
            <ListItem>losses that were not reasonably foreseeable when you started using the Site.</ListItem>
          </List>
          <Paragraph>
            If you are a consumer in the EEA, your statutory rights remain unaffected. If you are a business user, our total liability for all claims relating to your use of the Site will not exceed one hundred euros (€100).
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>12) Your responsibility (indemnity)</SectionTitle>
          <Paragraph>
            If you misuse the Site or breach these Terms and that causes us loss or a third-party claim against us, you agree - where permitted by law - to indemnify us against reasonable losses, costs, and liabilities arising from that breach or misuse.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>13) Intellectual property notices</SectionTitle>
          <Paragraph>
            "NexThera" and associated marks and logos are our trade names or trademarks. Other names and logos on the Site may be trademarks of their respective owners. No licence is granted except as set out in these Terms. Requests to use our brand assets or to reproduce content should be sent to <EmailLink href="mailto:mail@nexthera.tech">mail@nexthera.tech</EmailLink>.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>14) Export control and sanctions</SectionTitle>
          <Paragraph>
            By using the Site, you represent that you are not subject to EU, UK, or US sanctions that would prohibit your access. You agree not to use the Site in violation of applicable export-control or sanctions laws.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>15) Anti-bribery</SectionTitle>
          <Paragraph>
            You agree not to use the Site to make or receive bribes or unlawful payments and to comply with applicable anti-corruption laws.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>16) Force majeure</SectionTitle>
          <Paragraph>
            We won't be responsible for failure to perform any obligation under these Terms if the failure results from events outside our reasonable control, including failures of telecommunications, utilities, or hosting providers.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>17) Termination</SectionTitle>
          <Paragraph>
            We may suspend or terminate your access to the Site at any time where we reasonably believe you've breached these Terms, to protect the Site or other users, or to comply with law. You may stop using the Site at any time.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>18) Law and jurisdiction</SectionTitle>
          <Paragraph>
            These Terms and any dispute or claim (non-contractual or otherwise) arising from or related to them are governed by Portuguese law. If you are a business user, you and we agree to the non-exclusive jurisdiction of the courts of Porto, Portugal. If you are a consumer resident in the EEA, you may bring proceedings in your local courts as permitted by law.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>19) Contact</SectionTitle>
          <Paragraph>
            Questions about these Terms, or requests to use our content or brand assets, can be sent to <EmailLink href="mailto:mail@nexthera.tech">mail@nexthera.tech</EmailLink>. Our registered company details are available in our incorporation documents and the Portuguese registries.
          </Paragraph>
        </Section>
      </PolicyContent>
    </PolicyContainer>
  );
};

const PolicyContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8fafc, #ffffff);
  padding: 0;
`;

const PolicyHeader = styled.header`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #6b7280;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #2094F1;
  }
`;

const Logo = styled.img`
  height: 50px;
  width: auto;
  margin: 0;
`;

const PolicyContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const LastUpdated = styled.p`
  font-size: 0.9rem;
  color: #6b7280;
  text-align: center;
  margin-bottom: 3rem;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: #374151;
  margin-bottom: 1rem;
`;

const List = styled.ul`
  padding-left: 1.5rem;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  font-size: 1rem;
  line-height: 1.7;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const EmailLink = styled.a`
  color: #2094F1;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default TermsConditions;
